// A2LMapSearchPanel — admin-only MSV80 map lookup. Queries the same 9378-entry
// A2L index that grounds the AI Copilot, so the admin can find any map by
// name ("K_NMAX_NL1") or intent ("rev limiter", "cold start", "dtc cat").
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, Copy, Loader2, Database, Check } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Quick-jump pills for common intents. Click → runs the search.
const QUICK_QUERIES = [
  { label: 'Rev limiter',      q: 'NMAX' },
  { label: 'Cold start',       q: 'WARMLAUF' },
  { label: 'Catless DTCs',     q: 'KF_DIAG_MSO' },
  { label: 'VANOS / ghost cams', q: 'NWE' },
  { label: 'Decel burbles',    q: 'SCHUB' },
  { label: 'Top speed',        q: 'VMAX' },
  { label: 'Fuel / lambda',    q: 'LAM' },
  { label: 'Ignition advance', q: 'ZW' },
];

// Type-badge colors so scanning a results table stays fast
const TYPE_STYLE = {
  VALUE:   'text-cyan-400 border-cyan-500/40 bg-cyan-500/10',
  CURVE:   'text-purple-400 border-purple-500/40 bg-purple-500/10',
  MAP:     'text-emerald-400 border-emerald-500/40 bg-emerald-500/10',
  VAL_BLK: 'text-amber-400 border-amber-500/40 bg-amber-500/10',
  ASCII:   'text-zinc-300 border-zinc-500/40 bg-zinc-500/10',
};

function authHeaders() {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function A2LMapSearchPanel() {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const inputRef = useRef(null);

  // Debounce typed input so we're not hammering the server
  useEffect(() => {
    const id = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(id);
  }, [query]);

  const runSearch = useCallback(async (q) => {
    setError(null);
    if (!q) {
      setResult(null);
      return;
    }
    setLoading(true);
    try {
      const url = new URL(`${API_URL}/api/admin/a2l/search`);
      url.searchParams.set('q', q);
      url.searchParams.set('limit', '30');
      const res = await fetch(url.toString(), { headers: { ...authHeaders() } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setResult(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { runSearch(debounced); }, [debounced, runSearch]);

  // Initial load — fetch index totals so the header has context before any search
  useEffect(() => {
    fetch(`${API_URL}/api/admin/a2l/search?q=&limit=1`, { headers: { ...authHeaders() } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) setResult((prev) => prev || d); })
      .catch(() => { /* silent */ });
  }, []);

  const copy = (text, field) => {
    navigator.clipboard?.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 1200);
  };

  const maps = result?.maps || [];
  const totalMaps = result?.total_maps || 0;
  const ecu = result?.ecu || 'MSV80';

  return (
    <div className="bg-[#111111] border border-[#27272A] rounded-sm p-5" data-testid="a2l-search-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Database className="w-5 h-5 text-[#FF5E1A]" />
          <h3 className="font-['Outfit'] text-lg font-black text-white">A2L Map Search</h3>
          <span className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-wider px-1.5 py-0.5 border border-zinc-500/40 bg-zinc-500/10 text-zinc-400 rounded-sm">
            {ecu} · {totalMaps.toLocaleString()} maps indexed
          </span>
        </div>
      </div>

      <p className="text-[11px] text-zinc-500 mb-3 leading-relaxed">
        Live search against the parsed <code className="text-[#FF5E1A]">80B37E0E.a2l</code> — same
        index the AI Copilot uses. Type a map name or an intent ("rev limiter", "cold start"), copy
        the flash address, paste into WinOLS.
      </p>

      {/* Search input */}
      <div className="relative mb-3">
        <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name or intent…"
          data-testid="a2l-search-input"
          className="w-full bg-[#0A0A0A] border border-[#27272A] focus:border-[#FF5E1A] rounded-sm pl-10 pr-4 py-2.5 text-sm text-white font-['JetBrains_Mono'] outline-none transition-colors"
        />
        {loading && (
          <Loader2 className="w-4 h-4 text-[#FF5E1A] absolute right-3 top-1/2 -translate-y-1/2 animate-spin" />
        )}
      </div>

      {/* Quick-jump pills */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        {QUICK_QUERIES.map((qq) => (
          <button
            key={qq.q}
            type="button"
            onClick={() => { setQuery(qq.q); inputRef.current?.focus(); }}
            data-testid={`a2l-quickjump-${qq.q.toLowerCase()}`}
            className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-wider px-2.5 py-1 bg-[#0A0A0A] border border-[#27272A] hover:border-[#FF5E1A] hover:text-[#FF5E1A] text-zinc-400 rounded-sm transition-colors"
          >
            {qq.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-3 p-2 border border-red-500/40 bg-red-500/10 rounded-sm text-xs text-red-300">{error}</div>
      )}

      {/* Results */}
      {!query && (
        <div className="text-xs text-zinc-500 italic py-6 text-center">
          Start typing to search, or click a quick-jump pill above.
        </div>
      )}

      {query && maps.length === 0 && !loading && (
        <div className="text-xs text-zinc-500 italic py-6 text-center">
          No matches for "<span className="text-zinc-300 font-['JetBrains_Mono']">{query}</span>".
        </div>
      )}

      {maps.length > 0 && (
        <div className="space-y-1.5 max-h-[480px] overflow-y-auto">
          {maps.map((m) => {
            const typeClass = TYPE_STYLE[m.type] || TYPE_STYLE.VALUE;
            const addrCopied = copiedField === `${m.name}:addr`;
            const nameCopied = copiedField === `${m.name}:name`;
            return (
              <div
                key={m.name}
                className="group border border-[#27272A] bg-[#0A0A0A] hover:border-[#FF5E1A]/40 rounded-sm p-3 transition-colors"
                data-testid={`a2l-result-${m.name}`}
              >
                <div className="flex items-start gap-3">
                  <span className={`text-[9px] font-['JetBrains_Mono'] uppercase tracking-wider px-1.5 py-0.5 border rounded-sm whitespace-nowrap ${typeClass}`}>
                    {m.type}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        type="button"
                        onClick={() => copy(m.name, `${m.name}:name`)}
                        className="text-sm text-white font-bold font-['JetBrains_Mono'] hover:text-[#FF5E1A] transition-colors inline-flex items-center gap-1"
                        title="Click to copy map name"
                      >
                        {m.name}
                        {nameCopied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3 opacity-40 group-hover:opacity-100" />}
                      </button>
                      <button
                        type="button"
                        onClick={() => copy(m.address, `${m.name}:addr`)}
                        data-testid={`a2l-copy-addr-${m.name}`}
                        className="text-[11px] text-[#FF5E1A] font-['JetBrains_Mono'] hover:text-[#FF7A42] transition-colors inline-flex items-center gap-1 px-1.5 py-0.5 border border-[#FF5E1A]/30 hover:border-[#FF5E1A] bg-[#FF5E1A]/5 rounded-sm"
                        title="Click to copy flash address"
                      >
                        {m.address}
                        {addrCopied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                      </button>
                      <span className="text-[10px] font-['JetBrains_Mono'] text-zinc-500">
                        {m.size_bytes}B
                      </span>
                    </div>
                    {m.description && (
                      <div className="text-[11px] text-zinc-400 mt-1 leading-relaxed">{m.description}</div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-3 text-[10px] font-['JetBrains_Mono'] text-zinc-600">
        Flash base <span className="text-zinc-400">0xa1000000</span> · File offset = address − flash_base
      </div>
    </div>
  );
}
